import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './DayNightThemeToggle.css';

export type DayNightThemeToggleProps = {
  /** `true` lorsque le mode nuit est actif */
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
};

/**
 * Interrupteur jour / nuit discret pour le mode nuit dev.
 */
export const DayNightThemeToggle: React.FC<DayNightThemeToggleProps> = ({
  checked,
  onChange,
  disabled = false,
  id,
  'aria-label': ariaLabelProp,
}) => {
  const { t } = useTranslation('settings');
  const ariaLabel = ariaLabelProp ?? t('dayNightThemeToggle.toggleNightMode');

  const toggle = useCallback(() => {
    if (!disabled) onChange(!checked);
  }, [checked, disabled, onChange]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onChange(!checked);
      }
    },
    [checked, disabled, onChange]
  );

  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      className={`day-night-theme-toggle${checked ? ' day-night-theme-toggle--night' : ''}`}
      onClick={toggle}
      onKeyDown={onKeyDown}
    >
      <span className="day-night-theme-toggle__track">
        <svg className="day-night-theme-toggle__sun" viewBox="0 0 24 24" aria-hidden>
          <circle cx="12" cy="12" r="5" fill="currentColor" />
          <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none">
            <path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
          </g>
        </svg>
        <span className="day-night-theme-toggle__night-icons" aria-hidden>
          <svg viewBox="0 0 32 24" fill="none">
            <path
              d="M22 4.5a6.2 6.2 0 0 1-1.2 12.2 6.2 6.2 0 0 1-5.5-9.1 4.8 4.8 0 0 0 6.7-3.1Z"
              fill="currentColor"
              opacity="0.95"
            />
            <circle cx="8" cy="6" r="0.9" fill="currentColor" opacity="0.9" />
            <circle cx="12" cy="3" r="0.55" fill="currentColor" opacity="0.75" />
            <circle cx="5" cy="11" r="0.55" fill="currentColor" opacity="0.65" />
            <circle cx="26" cy="8" r="0.5" fill="currentColor" opacity="0.55" />
            <circle cx="28" cy="14" r="0.45" fill="currentColor" opacity="0.5" />
          </svg>
        </span>
        <span className="day-night-theme-toggle__knob" />
      </span>
    </button>
  );
};
